import { KeyValuePipe } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formatDistanceStrict } from 'date-fns';
import { hu } from 'date-fns/locale';
import { RefDirective } from 'rete-angular-plugin/20';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { WorkflowEditorService } from '../workflow-editor.service';
import { StateNode } from './state-node';

@Component({
  selector: 'app-state-node',
  templateUrl: './state-node.component.html',
  styleUrls: ['./state-node.component.scss'],
  imports: [MatIcon, RefDirective, KeyValuePipe, PayeeMiniIconButtonComponent],
  host: {
    '[class.selected]': 'isSelected()',
    '[class.active]': 'isActive()',
    '[style.background-color]': 'backgroundColor()',
    '[class.entry-point]': 'isEntryPoint()',
    '[class.refundable]': 'isRefundable()',
    '[style.color]': 'textColor()',
  },
})
export class StateNodeComponent implements AfterContentInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private workflowEditorService = inject(WorkflowEditorService);
  private snackbar = inject(MatSnackBar);

  private mouseX?: number;
  private mouseY?: number;

  @Input({ required: true }) data!: StateNode;
  @Input({ required: true }) emit!: (data: unknown) => void;
  @Input({ required: true }) rendered!: () => void;

  seed = 0;

  constructor() {
    this.cdr.detach();
  }

  isSelected(): boolean {
    return !!this.data?.selected;
  }

  isActive(): boolean {
    return this.data.is_active;
  }

  backgroundColor(): string {
    return this.data.state.color.background_color;
  }

  isEntryPoint(): boolean {
    return !!this.data.state.is_entry_point;
  }

  isRefundable(): boolean {
    return !!this.data.state.is_refundable;
  }

  textColor(): string {
    return this.data.state.color.text_color;
  }

  expectedTransitionDuration(at: string): string {
    return formatDistanceStrict(new Date(at), new Date(), { locale: hu });
  }

  @HostListener('mousedown', ['$event']) onMouseDown(event: MouseEvent) {
    this.mouseX = event.screenX;
    this.mouseY = event.screenY;
  }

  @HostListener('click', ['$event']) onClick(event: PointerEvent) {
    if (event.screenX !== this.mouseX || event.screenY !== this.mouseY) {
      return;
    }
    for (const node of this.data.editor.getNodes()) {
      if (node instanceof StateNode && node.is_active) {
        this.deselect(node);
      }
    }
    this.select();
  }

  async copyNodeText(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    event.preventDefault();

    try {
      await navigator.clipboard.writeText(this.data.state.label);
      this.snackbar.open('Címke a vágólapra másolva', 'OK', { duration: 3000 });
    } catch (error) {
      console.error('Failed to copy node label to clipboard', error);
    }
  }

  ngAfterContentInit(): void {
    this.data.width = this.elementRef.nativeElement.clientWidth;
    this.data.height = this.elementRef.nativeElement.clientHeight;
  }

  ngOnChanges(): void {
    this.cdr.detectChanges();
    requestAnimationFrame(() => this.rendered());
    this.seed++; // force render sockets
  }

  private select(node: StateNode = this.data): void {
    node.is_active = true;
    this.workflowEditorService.stateChange.next({
      selected: true,
      node,
    });
  }

  private deselect(node: StateNode = this.data): void {
    node.is_active = false;
    this.workflowEditorService.stateChange.next({
      selected: false,
      node,
    });
  }
}
